import React from 'react';
import { Box } from '@mui/material';
import { useRef, useState } from 'react';
import NodeMetricsInfo from '../../../../redux/containers/pipelineMonitoring/Node/MetricsInfo';
import NodeMetadataInfo from '../../../../redux/containers/pipelineMonitoring/Node/MetadataInfo';
import NodeProgressInfo from '../../../../redux/containers/pipelineMonitoring/Node/ProgressInfo';
import NodeTrafficInfo from '../../../../redux/containers/pipelineMonitoring/Node/TrafficInfo';
import NodeLogsInfo from '../../../../redux/containers/pipelineMonitoring/Node/LogsInfo';

import EdgeMetricsInfo from '../../../../redux/containers/pipelineMonitoring/Edge/MetricsInfo';
import EdgeMetadataInfo from '../../../../redux/containers/pipelineMonitoring/Edge/MetadataInfo';
import EdgeProgressInfo from '../../../../redux/containers/pipelineMonitoring/Edge/ProgressInfo';

import MonitoringActions from '../../../../redux/containers/pipelineMonitoring/MonitoringActions';
import RootMetricsInfo from '../../../../redux/containers/pipelineMonitoring/Root/MetricsInfo';
import { DRAWER } from '../../tokens';

const TAB_ID = (value) => `drawer-tab-${value}`;
const TABPANEL_ID = (value) => `drawer-tabpanel-${value}`;

function DrawerTab({ tab, active, tabRef, onSelect, onKeyDown }) {
  return (
    <Box
      ref={tabRef}
      id={TAB_ID(tab.value)}
      role="tab"
      aria-selected={active}
      aria-controls={TABPANEL_ID(tab.value)}
      // Roving tabindex: only the active tab is in the page tab order; the others are
      // reached with Arrow/Home/End keys (see handleTabKeyDown).
      tabIndex={active ? 0 : -1}
      onClick={() => onSelect(tab.value)}
      onKeyDown={onKeyDown}
      sx={{
        flexGrow: 1,
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        px: 2,
        py: 1.5,
        fontSize: 16,
        fontWeight: active ? 700 : 500,
        letterSpacing: 0.2,
        color: active ? DRAWER.text : DRAWER.textMuted,
        backgroundColor: active ? 'transparent' : DRAWER.surfaceAlt,
        borderBottom: `2px solid ${active ? DRAWER.accent : 'transparent'}`,
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': active
          ? {}
          : { color: DRAWER.text, backgroundColor: DRAWER.surfaceAltHover },
        '&:focus-visible': {
          outline: `2px solid ${DRAWER.accent}`,
          outlineOffset: '-2px',
        },
      }}
    >
      {tab.label}
    </Box>
  );
}

function NodeInfo(props) {
  const { object, node, edge, navOptions } = props;

  const isNode = object === 'node';
  const isEdge = object === 'edge';
  const isRoot = object === 'root';

  // Tabs available for the current object type, in display order.
  const tabs = isRoot
    ? [
        { value: 'metrics', label: 'Cloud' },
        { value: 'robot', label: 'Robot' },
      ]
    : [
        { value: 'metrics', label: 'Metrics' },
        { value: 'metadata', label: 'Metadata' },
        ...(isNode ? [{ value: 'traffic', label: 'Traffic' }] : []),
        { value: 'progress', label: 'Progress' },
        { value: 'logs', label: 'Logs' },
      ];

  const [selectedTab, setSelectedTab] = useState('metrics');
  // Fall back to the first tab when the current selection is not valid for this object
  // type (e.g. switching from a node to an edge while 'traffic' was selected). Keeps a
  // tab always active so the roving tabindex never leaves the tablist unreachable.
  const activeTab = tabs.some((t) => t.value === selectedTab)
    ? selectedTab
    : tabs[0].value;

  const tabRefs = useRef({});

  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        setSelectedTab(tabs[index].value);
        return;
      default:
        return;
    }
    event.preventDefault();
    const next = tabs[nextIndex].value;
    setSelectedTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <Box>
      <Box
        role="tablist"
        aria-label="Drawer sections"
        sx={{
          display: 'flex',
          mt: '60px',
          borderBottom: `1px solid ${DRAWER.border}`,
        }}
      >
        {tabs.map((tab, index) => (
          <DrawerTab
            key={tab.value}
            tab={tab}
            active={tab.value === activeTab}
            tabRef={(el) => {
              tabRefs.current[tab.value] = el;
            }}
            onSelect={setSelectedTab}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          />
        ))}
      </Box>

      <Box
        role="tabpanel"
        id={TABPANEL_ID(activeTab)}
        aria-labelledby={TAB_ID(activeTab)}
        tabIndex={0}
        sx={{ width: '500px' }}
      >
        {isNode ? (
          activeTab === 'metrics' ? (
            <NodeMetricsInfo node={node} />
          ) : activeTab === 'metadata' ? (
            <NodeMetadataInfo node={node} />
          ) : activeTab === 'traffic' ? (
            <NodeTrafficInfo node={node} />
          ) : activeTab === 'logs' ? (
            <NodeLogsInfo node={node} />
          ) : (
            <NodeProgressInfo node={node} />
          )
        ) : isEdge ? (
          activeTab === 'metrics' ? (
            <EdgeMetricsInfo edge={edge} />
          ) : activeTab === 'metadata' ? (
            <EdgeMetadataInfo edge={edge} />
          ) : activeTab === 'logs' ? null : (
            <EdgeProgressInfo edge={edge} />
          )
        ) : activeTab === 'metrics' ? (
          <RootMetricsInfo root={node} />
        ) : (
          <MonitoringActions node={node} navOptions={navOptions} />
        )}
      </Box>
    </Box>
  );
}

export default NodeInfo;
